import { Module, Mutation, Action } from "vuex-class-modules"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { store } from "../store"
import { Modules } from "../modules"
import { BaseModule } from "@/models/BaseModule"
import { AccountActions, accountModule } from "./account.module"

import {
  Goal,
  GOAL_TABLE_STATUSES,
  GoalStatus,
  GoalTableStatus,
  RoadmapGoalStatus
} from "@/models/Goal"
import { Feature } from "@/models/Feature"
import { ServerResponse } from "@/models/ServerResponse"
import { UpdateObjectValue } from "@/models/common"
import { FilterGoal, ServerPriorityResponse } from "@/models"
import { AlertActions, alertModule } from "./alert.module"
import { AlertContentItem, AlertType } from "@/models/Alert"
import { parsingServerResponseContent } from "@/utils/helpers"
import { projectModule } from "@/store/modules/project.module"

type GoalStatusResponseState = "currentGoals" | "nextInLineGoals" | "futureGoals"

/** Roadmap column responses by Goal status; each column paginates on its own. */
const GoalStatusResponseStateMap: Partial<Record<GoalStatus, GoalStatusResponseState>> = {
  CURRENT: "currentGoals",
  NEXT_IN_LINE: "nextInLineGoals",
  FUTURE: "futureGoals"
}

export type GoalStatusCounts = Record<GoalTableStatus, number>

const createGoalStatusCounts = (): GoalStatusCounts => ({
  CURRENT: 0,
  NEXT_IN_LINE: 0,
  FUTURE: 0,
  CLOSE: 0
})

export enum GoalActions {
  GET_GOALS = "GET_GOALS",
  GET_RESPONSE_GOAL = "GET_RESPONSE_GOAL",
  GET_CURRENT_GOAL_RESPONSE = "GET_CURRENT_GOAL_RESPONSE",
  GET_CURRENT_GOAL = "GET_CURRENT_GOAL",
  GET_NEXT_IN_LINE_GOAL_RESPONSE = "GET_NEXT_IN_LINE_GOAL_RESPONSE",
  GET_FUTURE_GOAL_RESPONSE = "GET_FUTURE_GOAL_RESPONSE",
  GET_TABLE_GOAL_RESPONSE = "GET_TABLE_GOAL_RESPONSE",
  GET_GOAL_STATUS_COUNTS = "GET_GOAL_STATUS_COUNTS",
  GET_ROADMAP_GOALS = "GET_ROADMAP_GOALS",
  GET_TABLE_GOALS = "GET_TABLE_GOALS",
  LOAD_GOAL_STATUS_COUNTS = "LOAD_GOAL_STATUS_COUNTS",
  SET_FEATURE_BY_GOAL = "SET_FEATURE_BY_GOAL",
  GET_GOAL = "GET_GOAL",
  GET_EXTENDED_GOAL = "GET_EXTENDED_GOAL",
  SET_GOAL = "SET_GOAL",
  GET_GOALS_LIST = "GET_GOALS_LIST",
  GET_GOALS_SEARCH = "GET_GOALS_SEARCH",
  CREATE_GOAL = "CREATE_GOAL",
  UPDATE_GOAL = "UPDATE_GOAL",
  UPDATE_EXTENDED_GOAL = "UPDATE_EXTENDED_GOAL",
  DELETE_GOAL_ATTACHMENT = "DELETE_GOAL_ATTACHMENT",
  GET_FEATURE_BY_GOAL = "GET_FEATURE_BY_GOAL",
  UPDATE_GOAL_VALUE = "UPDATE_GOAL_VALUE",
  RESET_GOAL = "RESET_GOAL",
  UPDATE_GOALS_DRAGGABLE_LIST = "UPDATE_GOALS_DRAGGABLE_LIST",
  UPDATE_FEATURE_DRAGGABLE_LIST_IN_GOAL = "UPDATE_FEATURE_DRAGGABLE_LIST_IN_GOAL",
  UPDATE_GOAL_STATUS = "UPDATE_GOAL_STATUS",
  CHANGE_GOAL_PRIORITY = "CHANGE_GOAL_PRIORITY",
  UPDATE_FEATURE_IN_GOAL = "UPDATE_FEATURE_IN_GOAL",
  UPDATE_GOAL_FEATURE_VALUE = "UPDATE_GOAL_FEATURE_VALUE",
  UPDATE_ROADMAP_GOAL_IN_LIST = "UPDATE_ROADMAP_GOAL_IN_LIST",
  UPDATE_GOALS_LIST = "UPDATE_GOALS_LIST",
  REMOVE_GOAL_FROM_LIST = "REMOVE_GOAL_FROM_LIST",
  SUBSCRIPTION_GOAL_CHANGE = "SUBSCRIPTION_GOAL_CHANGE",
  ADD_SPECIFIC_FEATURE = "ADD_SPECIFIC_FEATURE",
  DELETE_FEATURE_BY_GOAL = "DELETE_FEATURE_BY_GOAL",
}

@Module
class GoalModule extends BaseModule {
  goalsListResponse: ServerResponse<Goal> = new ServerResponse<Goal>()

  currentGoalsResponse: ServerResponse<Goal> = new ServerResponse<Goal>()
  nextInLineGoalsResponse: ServerResponse<Goal> = new ServerResponse<Goal>()
  futureGoalsResponse: ServerResponse<Goal> = new ServerResponse<Goal>()
  tableGoalsResponse: ServerResponse<Goal> = new ServerResponse<Goal>()
  goalStatusCounts: GoalStatusCounts = createGoalStatusCounts()

  goal: Goal = Goal.create()
  featureInGoal: { [key: string]: Feature[] } = {}

  get accountId() {
    return accountModule.accountId
  }

  get projectId() {
    return projectModule.currentProjectId
  }


  get [`get/${GoalActions.GET_FEATURE_BY_GOAL}`]() {
    return (goalId: string) => this.featureInGoal[goalId] ?? []
  }

  get [`get/${GoalActions.GET_RESPONSE_GOAL}`]() {
    return this.goalsListResponse
  }

  get [`get/${GoalActions.GET_CURRENT_GOAL_RESPONSE}`]() {
    return this.currentGoalsResponse
  }

  get [`get/${GoalActions.GET_NEXT_IN_LINE_GOAL_RESPONSE}`]() {
    return this.nextInLineGoalsResponse
  }

  get [`get/${GoalActions.GET_FUTURE_GOAL_RESPONSE}`]() {
    return this.futureGoalsResponse
  }

  get [`get/${GoalActions.GET_TABLE_GOAL_RESPONSE}`]() {
    return this.tableGoalsResponse
  }

  get [`get/${GoalActions.GET_GOAL_STATUS_COUNTS}`]() {
    return this.goalStatusCounts
  }

  get [`get/${GoalActions.GET_CURRENT_GOAL}`]() {
    return this.currentGoalsResponse?.content?.[0]
  }

  get [`get/${GoalActions.GET_GOAL}`]() {
    return this.goal
  }

  get [`get/${GoalActions.GET_GOALS}`]() {
    return this.goalsListResponse.content
  }

  get [`get/${GoalActions.GET_GOALS_LIST}`]() {
    return this.goalsListResponse
  }

  @Mutation
  setGoalFeatures({ goalId, features }: { goalId: string, features: Feature[] }) {
    this.featureInGoal[goalId] = features
  }

  @Mutation
  updateFeatureInGoal({ goalId, feature }: { goalId: string, feature: Feature }) {
    this.featureInGoal[goalId] = this.featureInGoal[goalId].map(f => (f.id !== feature.id ? f : feature))
  }

  @Mutation
  deleteFeatureFromGoal({ goalId, feature }: { goalId: string, feature: Feature }) {
    this.featureInGoal[goalId] = this.featureInGoal[goalId].filter(f => f.id !== feature.id)
  }

  @Mutation
  setGoalsListResponse(serverResponse: ServerResponse<Goal>) {
    const serverResponseContent = serverResponse.content.map(goal => Goal.create(goal))
    const content = parsingServerResponseContent({
      originalContent: this.goalsListResponse.content,
      serverResponseContent,
      numberOfPage: serverResponse.number,
      originalNumberOfPage: this.goalsListResponse.number,
    })

    this.goalsListResponse = {
      ...serverResponse,
      content
    } as ServerResponse<Goal>
  }

  @Mutation
  setGoalsByStatus({
    serverResponse,
    status,
  }:{serverResponse: ServerResponse<Goal>, status: string}) {
    const content = parsingServerResponseContent({
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.number,
      originalContent: this[status + "Response"].content,
      originalNumberOfPage: this[status + "Response"].number,
    })

    this[status + "Response"] = {
      ...serverResponse,
      content
    }
  }

  @Mutation
  addGoalsByStatus({ content, status }) {
    this[status] = [...this[status], ...content]
  }

  /** Applies a drag-and-drop result; the total keeps counting goals on pages not loaded yet. */
  @Mutation
  setGoalsByType({ content, status }) {
    const response: ServerResponse<Goal> = this[status + "Response"]
    const difference = content.length - response.content.length

    Object.assign(response, {
      content,
      totalElements: Math.max(0, response.totalElements + difference),
    })
  }

  @Mutation
  setTableGoalsResponse(serverResponse: ServerResponse<Goal>) {
    const content = parsingServerResponseContent({
      originalContent: this.tableGoalsResponse.content,
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.number,
      originalNumberOfPage: this.tableGoalsResponse.number,
    })

    this.tableGoalsResponse = {
      ...serverResponse,
      content
    } as ServerResponse<Goal>
  }

  @Mutation
  setGoalStatusCounts(counts: GoalStatusCounts) {
    this.goalStatusCounts = counts
  }

  /** Keeps a status-changed table row and the status chip counters in sync without a reload. */
  @Mutation
  updateGoalInTable({ goal, previousStatus }: { goal: Goal, previousStatus: GoalStatus }) {
    const index = this.tableGoalsResponse.content.findIndex(item => item.id === goal.id)
    if (index !== -1) {
      this.tableGoalsResponse.content.splice(index, 1, goal)
    }

    if (previousStatus === goal.status) return

    const counts = { ...this.goalStatusCounts }
    const isCounted = (status: GoalStatus): status is GoalTableStatus =>
      GOAL_TABLE_STATUSES.includes(status as GoalTableStatus)

    if (isCounted(previousStatus)) {
      counts[previousStatus] = Math.max(0, counts[previousStatus] - 1)
    }
    if (isCounted(goal.status)) {
      counts[goal.status] += 1
    }
    this.goalStatusCounts = counts
  }

  updateGoalInResponse({ goal, response }: { goal: Goal, response: ServerResponse<Goal> }) {
    const content: Goal[] = response.content
    if (content.find(g => g.id === goal.id)) {
      const index = content.findIndex(g => g.id === goal.id)
      content[index] = goal
    } else {
      content.push(goal)
      response.totalElements += 1
    }
  }

  /**
   * Adds a created Goal to, or replaces an edited Goal in, the Roadmap column of its status,
   * and refreshes its All Goals table row when that row is loaded.
   */
  @Mutation
  updateRoadmapGoalInList({ goal } : { goal: Goal }) {
    const tableIndex = this.tableGoalsResponse.content.findIndex(item => item.id === goal.id)
    if (tableIndex !== -1) {
      this.tableGoalsResponse.content.splice(tableIndex, 1, goal)
    }

    const state = GoalStatusResponseStateMap[goal.status]
    if (!state) return

    this.updateGoalInResponse({
      goal,
      response: this[state + "Response"],
    })
  }

  @Mutation
  updateGoalsList({ goal } : { goal: Goal }) {
    this.updateGoalInResponse({
      goal,
      response: this.goalsListResponse,
    })
  }

  /** Mirrors a changed Goal only across Roadmap responses where it was already visible. */
  @Mutation
  updateGoalInVisibleLists({ goal }: { goal: Goal }) {
    const responses = Object.entries(GoalStatusResponseStateMap).map(([status, state]) => ({
      status,
      response: this[state + "Response"] as ServerResponse<Goal>
    }))
    const tableIndex = this.tableGoalsResponse.content.findIndex(item => item.id === goal.id)
    if (tableIndex !== -1) {
      this.tableGoalsResponse.content.splice(tableIndex, 1, goal)
    }

    const wasVisible = responses.some(({ response }) => response.content.some(item => item.id === goal.id))

    if (!wasVisible) return

    responses.forEach(({ status, response }) => {
      const index = response.content.findIndex(item => item.id === goal.id)
      if (status === goal.status) {
        // A level change on the Goal screen also shows the goal in its new column (appended).
        this.updateGoalInResponse({ goal, response })
      } else if (index !== -1) {
        response.content.splice(index, 1)
        response.totalElements = Math.max(0, response.totalElements - 1)
      }
    })
  }

  @Mutation
  removeGoalFromList({ goalId, status }: { goalId: string, status: GoalStatus }) {
    const listStatus = GoalStatusResponseStateMap[status]
    if (!listStatus) return

    const content: Goal[] = this[listStatus + "Response"].content
    const index = content.findIndex(g => g.id === goalId)
    if (index !== -1) {
      content.splice(index, 1)
      const response: ServerResponse<Goal> = this[listStatus + "Response"]
      response.totalElements = Math.max(0, response.totalElements - 1)
    }
  }

  /**
   * Shows a Goal changed by someone else (`GOAL_CHANGED`) at `index` of its Roadmap column;
   * `index` is null when the Goal belongs after the loaded pages.
   */
  @Mutation
  setGoalFromSubscription({ goal, index, previousStatus }: {
    goal: Goal
    index: number | null
    previousStatus: GoalStatus | null
  }) {
    let shownState: string | null = null
    for (const state of Object.values(GoalStatusResponseStateMap)) {
      const content: Goal[] = this[state + "Response"].content
      const position = content.findIndex(item => item.id === goal.id)
      if (position !== -1) {
        content.splice(position, 1)
        shownState = state
      }
    }

    const previousState = previousStatus && GoalStatusResponseStateMap[previousStatus]
    const state = GoalStatusResponseStateMap[goal.status]
    if (previousStatus !== goal.status) {
      const previousResponse: ServerResponse<Goal> | null = previousState
        ? this[previousState + "Response"]
        : null
      // The user's own create, move or close is already counted: the goal is shown in its new
      // column, or it is gone from a fully loaded previous column.
      const isCountedAlready = shownState
        ? shownState === state
        : !!previousResponse && previousResponse.content.length >= previousResponse.totalElements
      if (!isCountedAlready) {
        const fromState = shownState ?? previousState
        if (fromState) {
          const response: ServerResponse<Goal> = this[fromState + "Response"]
          response.totalElements = Math.max(0, response.totalElements - 1)
        }
        if (state) this[state + "Response"].totalElements += 1
      }
    }
    if (state && index !== null) this[state + "Response"].content.splice(index, 0, goal)

    const tableIndex = this.tableGoalsResponse.content.findIndex(item => item.id === goal.id)
    if (tableIndex !== -1) this.tableGoalsResponse.content.splice(tableIndex, 1, goal)
  }

  @Mutation
  setGoalValue(payload: UpdateObjectValue<Goal>) {
    Object.assign(this.goal, { [payload["id"]]: payload["value"] })
  }

  @Mutation
  updateFeatureValue({ payload, featureId, goalId }: { payload: UpdateObjectValue<Feature>, featureId: string, goalId: string }) {
    // TODO - Replace with update method
    this.featureInGoal[goalId] = this.featureInGoal[goalId].map((el) => {
      if (el.id === featureId) el[payload?.["id"]] = payload?.["value"]
      return el
    })
  }

  @Mutation
  setGoal(goal: Goal) {
    this.goal = goal
  }

  @Mutation
  resetGoal() {
    this.goal = Goal.create()
  }

  @Action
  async [GoalActions.GET_GOALS](filterGoal: FilterGoal) {
    const filter = FilterGoal.create(filterGoal).getJsonObj()

    const data = await apiService.get<ServerResponse<Goal>>(Endpoint.GET_GOAL(this.accountId), filter)
    return this.handleResponse<ServerResponse<Goal>>(data, (data) => {
      const content: Goal[] = data.content.map(goal => Goal.create(goal))
      const serverResponse = {
        ...data,
        content,
      } as ServerResponse<Goal>

      this.setGoalsListResponse(serverResponse)
      return this.goalsListResponse
    })
  }

  @Action
  async [GoalActions.GET_GOALS_SEARCH]({ filterGoal, onlyGet }: {filterGoal: FilterGoal, onlyGet?: boolean }) {
    const filter = filterGoal.getJsonObj()

    const data = await apiService.get<ServerResponse<Goal>>(Endpoint.SEARCH_GOAL(this.accountId), filter)
    return this.handleResponse<ServerResponse<Goal>>(data, (data) => {
      const content: Goal[] = data.content.map(goal => Goal.create(goal))
      const serverResponse = {
        ...data,
        content,
      } as ServerResponse<Goal>

      if(onlyGet){
        return serverResponse as ServerResponse<Goal>
      }

      this.setGoalsListResponse(serverResponse)

      return serverResponse as ServerResponse<Goal>
    })
  }

  /**
   * Loads one page of a Roadmap column (Current, Next in Line or Future).
   *
   * The column response is the only state written here, so each column keeps its own page.
   */
  @Action
  async [GoalActions.GET_ROADMAP_GOALS]({ filterGoal, status }: { filterGoal: FilterGoal, status: RoadmapGoalStatus }) {
    const state = GoalStatusResponseStateMap[status]
    const filter = FilterGoal.create({ ...filterGoal, statuses: [status] })
    const data = await apiService.get<ServerResponse<Goal>>(Endpoint.SEARCH_GOAL(this.accountId), filter.getJsonObj())

    return this.handleResponse<ServerResponse<Goal>>(data, (data) => {
      const serverResponse = {
        ...data,
        content: data.content.map(goal => Goal.create(goal)),
      } as ServerResponse<Goal>

      if (state) {
        this.setGoalsByStatus({ serverResponse, status: state })
      }

      return serverResponse
    }, false, () => {
      // A failed first page must not leave the previous filter's goals in the column.
      if (state && filter.page === 1) {
        this.setGoalsByStatus({ serverResponse: new ServerResponse<Goal>(), status: state })
      }
      return false
    })
  }

  /** Loads one page of the All Goals table; an empty status list means every status. */
  @Action
  async [GoalActions.GET_TABLE_GOALS](filterGoal: FilterGoal) {
    const data = await apiService.get<ServerResponse<Goal>>(Endpoint.SEARCH_GOAL(this.accountId), filterGoal.getJsonObj())

    return this.handleResponse<ServerResponse<Goal>>(data, (data) => {
      const serverResponse = {
        ...data,
        content: data.content.map(goal => Goal.create(goal)),
      } as ServerResponse<Goal>

      this.setTableGoalsResponse(serverResponse)
      return serverResponse
    }, false, () => {
      if (filterGoal.page === 1) {
        this.setTableGoalsResponse(new ServerResponse<Goal>())
      }
      return false
    })
  }

  /**
   * Loads the per-status totals for the All Goals filter chips in ONE request.
   *
   * The backend returns `{ STATUS: count }` for every status.
   */
  @Action
  async [GoalActions.LOAD_GOAL_STATUS_COUNTS]({ projectId, demo }: { projectId?: string, demo?: boolean }) {
    const counts = createGoalStatusCounts()
    const params = new URLSearchParams()
    if (projectId) params.append("projectId", projectId)
    params.append("demo", String(!!demo))
    const data = await apiService.get<Partial<GoalStatusCounts>>(
      Endpoint.GOAL_STATUS_COUNTS(this.accountId), params
    )

    this.handleResponse<Partial<GoalStatusCounts>>(data, (data) => {
      GOAL_TABLE_STATUSES.forEach(status => {
        counts[status] = data[status] ?? 0
      })
    })

    this.setGoalStatusCounts(counts)
    return counts
  }

  @Action
  async [GoalActions.GET_GOALS_LIST]({
    filterGoal,
    projectId,
    skipStoreUpdate
  }: {
    filterGoal: FilterGoal
    projectId?: string
    skipStoreUpdate?: boolean
  }) {
    const selectedProjectId = projectId || filterGoal.projectId || this.projectId
    const data = await apiService.get<ServerResponse<Goal>>(
      Endpoint.GET_GOALS_LIST(selectedProjectId),
      filterGoal.getJsonObj()
    )
    return this.handleResponse<ServerResponse<Goal>>(data, data => {
      if (skipStoreUpdate) {
        return {
          ...data,
          content: data.content.map(goal => Goal.create(goal)),
        } as ServerResponse<Goal>
      }

      this.setGoalsListResponse(data)
      return this.goalsListResponse
    })
  }

  @Action
  async [GoalActions.CREATE_GOAL](goal: Goal) {
    const data = await apiService.post<Goal>(Endpoint.POST_GOAL(goal.projectId), goal.getJsonObj())

    return this.handleResponse<Goal>(data, (data) => {
      const goal = Goal.create(data)
      this.setGoal(goal)
      const alertContent: AlertContentItem[] = [
        { text: "Goal", type: "bold" },
        { text: " successfully created", type: "regular" },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      return goal
    })
  }

  @Action
  async [GoalActions.UPDATE_GOAL](payload: Goal | { goal: Goal, skipSetGoal?: boolean }) {
    const rawGoal = 'goal' in payload ? payload.goal : payload
    const skipSetGoal = 'skipSetGoal' in payload ? payload.skipSetGoal : false
    const goal = rawGoal instanceof Goal
      ? rawGoal
      : Goal.create(rawGoal)
    const data = await apiService.put<Goal>(Endpoint.GOAL(goal.projectId, goal.id), goal.getJsonObj())
    return this.handleResponse<Goal>(data, (data) => {
      const updatedGoal = Goal.create(data)
      if (!skipSetGoal) {
        this.setGoal(updatedGoal)
      }
      if (updatedGoal.status !== "CLOSE") {
        const alertContent: AlertContentItem[] = [{
          text: "Goal updated",
          type: "regular",
        }]
        alertModule[AlertActions.SHOW_ALERT]({
          type: AlertType.SUCCESS,
          theme: "toast",
          content: alertContent,
        })
      }

      return updatedGoal
    })
  }

  @Action
  async [GoalActions.UPDATE_EXTENDED_GOAL](payload: Goal | { goal: Goal, skipSetGoal?: boolean }) {
    const rawGoal = "goal" in payload ? payload.goal : payload
    const skipSetGoal = "skipSetGoal" in payload ? payload.skipSetGoal : false
    const goal = rawGoal instanceof Goal
      ? rawGoal
      : Goal.create(rawGoal)
    const data = await apiService.put<Goal>(
      Endpoint.UPDATE_EXTENDED_GOAL(goal.projectId, goal.id),
      goal.getJsonObj()
    )

    return this.handleResponse<Goal>(data, data => {
      const updatedGoal = Goal.create(data)

      if (!skipSetGoal) {
        this.setGoal(updatedGoal)
      }

      return updatedGoal
    })
  }

  @Action
  async [GoalActions.DELETE_GOAL_ATTACHMENT]({
    goalId,
    fileMetadataId,
    projectId
  }: {
    goalId: string
    fileMetadataId: string
    projectId?: string
  }) {
    const data = await apiService.delete<void>(
      Endpoint.DELETE_GOAL_ATTACHMENT(projectId ?? this.projectId, goalId, fileMetadataId)
    )
    return this.handleResponse<void>(data)
  }

  @Action
  async [GoalActions.GET_GOAL]({ id, projectId, onlyGet } : { id: string, projectId?: string, onlyGet?: boolean }) {
    const selectedProjectId = projectId ? projectId : this.projectId
    const data = await apiService.get<Goal>(Endpoint.GOAL(selectedProjectId, id))
    return this.handleResponse<Goal>(data, (data) => {
      const goal = Goal.create(data)
      if(!onlyGet){
        this.setGoal(goal)
      }
      return goal
    })
  }

  @Action
  async [GoalActions.GET_EXTENDED_GOAL]({ id, onlyGet } : { id: string, onlyGet?: boolean }) {
    const data = await apiService.get<Goal>(Endpoint.GET_EXTENDED_GOAL(this.accountId, id))
    return this.handleResponse<Goal>(data, (data) => {
      const goal = Goal.create(data)

      this.setGoalFeatures({
        goalId: goal.id,
        features: goal.features ?? []
      })

      if(!onlyGet){
        this.setGoal(goal)
      }
      return goal
    })
  }

  @Action
  async [GoalActions.UPDATE_GOAL_STATUS]({ goal, status }: { goal: Goal, status: GoalStatus }) {
    const previousStatus = goal.status
    const updatedGoal = await this[GoalActions.UPDATE_EXTENDED_GOAL]({
      goal: Object.assign(goal, { status }),
      skipSetGoal: true
    })

    if (updatedGoal && previousStatus) {
      this.updateGoalInTable({ goal, previousStatus })
    }

    if (updatedGoal && status === "CLOSE") {
      const alertContent: AlertContentItem[] = [
        { text: "Goal ", type: "bold" },
        { text: "was closed", type: "regular" }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    }

    return updatedGoal
  }

  @Action
  async [GoalActions.UPDATE_FEATURE_IN_GOAL](payload: { goalId: string, feature: Feature }) {
    this.updateFeatureInGoal(payload)
  }

  @Action
  async [GoalActions.UPDATE_ROADMAP_GOAL_IN_LIST]({ goal }: { goal: Goal }) {
    this.updateRoadmapGoalInList({
      goal
    })
  }

  /** Updates Milestone details and any cached Roadmap card from the same server result. */
  @Action
  async [GoalActions.UPDATE_GOALS_LIST]({ goal }: { goal: Goal }) {
    this.updateGoalsList({
      goal
    })
    this.updateGoalInVisibleLists({ goal })
  }

  @Action
  async [GoalActions.SUBSCRIPTION_GOAL_CHANGE](payload: {
    goal: Goal
    index: number | null
    previousStatus: GoalStatus | null
  }) {
    this.setGoalFromSubscription(payload)
  }

  @Action
  async [GoalActions.REMOVE_GOAL_FROM_LIST]({ goalId, status }: { goalId: string, status: GoalStatus }) {
    this.removeGoalFromList({ goalId, status })
  }

  @Action
  async [GoalActions.GET_FEATURE_BY_GOAL]({ goal, skipStoreUpdate }: { goal: Goal, skipStoreUpdate?: boolean }) {
    const { projectId, id } = goal
    const data = await apiService.get<Feature[]>(Endpoint.GET_FEATURE_BY_GOAL(projectId, id))
    return this.handleResponse<Feature[]>(data, (data) => {
      const feature = data.map(feature => Feature.create(feature))
      if (!skipStoreUpdate) {
        this.setGoalFeatures({
          goalId: id,
          features: feature,
        })
      }
      return feature
    })
  }

  @Action
  async [GoalActions.SET_FEATURE_BY_GOAL]({ goal, features, alertMessage }: { goal: Goal, features: Feature[], alertMessage?: AlertContentItem[] }) {
    const { id, projectId } = goal
    const data = await apiService.put<Goal>(Endpoint.FEATURE_GOAL(projectId, id), features.length ? features.map(item => item.id) : [])
    return this.handleResponse<Goal>(data, (data) => {
      const newGoal = Goal.create(data)

      if (alertMessage) {
        alertModule[AlertActions.SHOW_ALERT]({
          type: AlertType.SUCCESS,
          theme: "toast",
          content: alertMessage,
        })
      }

      return newGoal
    })
  }

  @Action
  async [GoalActions.CHANGE_GOAL_PRIORITY]({ element, nextElement, previousElement }: { element: Feature, nextElement: Feature, previousElement: Feature }) {
    const data = await apiService.patch<ServerPriorityResponse>(Endpoint.CHANGE_GOAL_PRIORITY(element.projectId), {
      previousItemId: previousElement?.id || null,
      nextItemId: nextElement?.id || null,
      itemId: element.id,
    })
    return this.handleResponse<ServerPriorityResponse>(data, (data) => {
      if (data.normalizedAll) return "reload"
    })
  }

  @Action
  async [GoalActions.ADD_SPECIFIC_FEATURE]({ goal, feature, alertMessage }: { goal: Goal, feature: Feature, alertMessage?: AlertContentItem[] }) {
    const data = await apiService.post<Goal>(Endpoint.ADD_SPECIFIC_FEATURE(goal.projectId, goal.id, feature.id))
    return this.handleResponse<Goal>(data, (data) => {
      if (alertMessage) {
        alertModule[AlertActions.SHOW_ALERT]({
          type: AlertType.SUCCESS,
          theme: "toast",
          content: alertMessage,
        })
      }
      return Goal.create(data)
    })
  }

  @Action
  [GoalActions.UPDATE_GOAL_VALUE](payload: UpdateObjectValue<Goal>) {
    this.setGoalValue(payload)
  }

  @Action
  [GoalActions.RESET_GOAL]() {
    this.resetGoal()
  }

  @Action
  [GoalActions.SET_GOAL](goal: Goal) {
    this.setGoal(goal)
  }

  @Action
  async [GoalActions.UPDATE_FEATURE_DRAGGABLE_LIST_IN_GOAL]({ draggableList, goalId }: { draggableList: Feature[], goalId: string }) {
    this.setGoalFeatures({
      goalId,
      features: draggableList,
    })
  }

  @Action
  async [GoalActions.DELETE_FEATURE_BY_GOAL]({ feature, goalId }: { feature: Feature, goalId: string }) {
    this.deleteFeatureFromGoal({
      goalId,
      feature,
    })
  }

  @Action
  async [GoalActions.UPDATE_GOALS_DRAGGABLE_LIST]({ draggableList, status }: { draggableList: Feature[], status: GoalStatus }) {
    const statusResponseState = GoalStatusResponseStateMap[status]
    if (!statusResponseState) return

    this.setGoalsByType({
      status: statusResponseState,
      content: draggableList,
    })
  }

  @Action
  async [GoalActions.UPDATE_GOAL_FEATURE_VALUE]({ payload, featureId, goalId }: { payload: UpdateObjectValue<Feature>, featureId: string, goalId: string }) {
    this.updateFeatureValue({ payload, featureId, goalId })
  }
}

export const goalModule = new GoalModule({
  store,
  name: Modules.GOAL,
})
